/**
 * @file ProjectContent.jsx
 *
 * @component ProjectContent
 * @description This component renders project specific views for a selected project.
 */

import { useEffect, useRef, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";
import { useSubscription } from "@apollo/client";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { GET_PROJECT_EVENTS } from "../../graphql/subscription";
import { useSnackbar } from "../../hooks";
import {
  useDeleteTime,
  useGetProjectAndTasks,
  useReportTime,
  useTaskMutationActions,
} from "../../hooks/Projects";
import ConfirmDeleteModal from "../common/ConfirmDeleteModal";
import WorkspaceDashboard from "../WorkspaceDashboard";
import { TaskAction, TimeLogAction } from "./helper";
import useProjectViews from "./hooks/useProjectViews";
import useRemainingHeight from "./hooks/useRemainingHeight";
import Kanban from "./Kanban";
import ProjectContentHead from "./ProjectContentHead";
import ProjectFileView from "./ProjectFileView";
import ProjectModal from "./ProjectModal/ProjectModal";
import ProjectViewPanel from "./ProjectViews/ProjectViewPanel";
import ProjectViewSelector from "./ProjectViews/ProjectViewSelector";
import { ProjectViewType, default as ProjectViewUtils } from "./ProjectViews/projectViewUtils";
import ReportsPage from "./Reports/ReportsPage";
import TableView from "./TableView";
import TaskDetailsBase from "./TaskDetails/TaskDetailsBase";
import TaskDetailsColumn from "./TaskDetails/TaskDetailsColumn";
import TaskDetailsDrawer from "./TaskDetailsDrawer";
import TaskDetailsModal from "./TaskDetailsModal";

/**
 * Minimum height for the ReportsPage component parentHeight prop.
 */
const MIN_REPORTS_PAGE_PARENT_HEIGHT_VALUE = 200;

/**
 * Styling for the root element.
 * 
 * NOTE: Any changes to the padding  will affect the "magicOffset" used by the "useRemainingHeight" hook.
 */
const rootStyles = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  p: 3,
};

const ProjectContent = ({
  drawerRef,
  enableEdit = true,
  initialFile,
  onUpdated,
  projectId,
  siteId,
}) => {
  const location = useLocation();
  const snackbar = useSnackbar();

  const projectContentHeadRef = useRef(null);
  const projectViewSelectorRef = useRef(null);

  const [project, setProject] = useState();

  const {
    activeTabIndex,
    createPanelProps,
    isActivePanel,
    onSelectView,
    projectTabs,
    resetProjectViews,
    viewSettings,
  } = useProjectViews(project);

  const [fullScreen, setFullScreen] = useState(false);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [taskActionData, setTaskActionData] = useState();
  const [taskDetails, setTaskDetails] = useState();
  const [taskDetailsColumns, setTaskDetailsColumns] = useState();
  const [tasks, setTasks] = useState([]);
  const [userLookup, setUserLookup] = useState([]);

  const [modals, setModals] = useState({
    confirmDeleteTaskModal: false,
    projectEditModal: false,
    taskDetailsModal: false,
  });

  const [getProjectAndTasks, { data: projectData, error: projectError, loading: projectLoading }] = useGetProjectAndTasks();

  const { createTask, deleteTask, moveTask, updateTask, loading: taskLoading } = useTaskMutationActions();

  const [reportTime, { loading: timeReportLoading }] = useReportTime();
  const [deleteTime, { loading: timeReportDeleteLoading }] = useDeleteTime();

  const reportsPageParentHeight = useRemainingHeight({
    observedRef: drawerRef,
    headerRefs: [projectContentHeadRef, projectViewSelectorRef],
    isActive: isActivePanel(ProjectViewType.Reports),
    //disableUpdate: fullScreen,
    minReportedHeight: MIN_REPORTS_PAGE_PARENT_HEIGHT_VALUE,
  });

  const accessToken = localStorage.getItem("access_token");

  const deleteTaskAction = (taskId) => handleTaskAction({ action: TaskAction.DELETE, data: { taskId } });

  const { data: eventData, loading: eventLoading } = useSubscription(GET_PROJECT_EVENTS, {
    variables: {
      projectId: projectId,
      authToken: accessToken,
    },
    onData: function (update) {

      if (update?.data?.data?.projectEvents) {

        let data = update?.data?.data?.projectEvents;
        console.log({ data });

        switch (data.eventType) {
          case "TASK_UPDATED":
            const updatedTask = data.task;
            const updatedTasks = tasks.map(task =>
              task.id === updatedTask.id ? updatedTask : task
            );

            setTasks(updatedTasks);

            if (taskDetails?.id === updatedTask.id) {
              setTaskDetails({ ...taskDetails, comments: updatedTask.comments });
            }

            break;
          case "TASK_CREATED":
            const newTask = data.task;
            if (!tasks.find(task => task.id === newTask.id)) {
              setTasks(prevTasks => [newTask, ...prevTasks]);
            }
            break;
          case "TASK_DELETED":
            const deletedTaskId = data.taskId;
            setTasks(prevTasks => prevTasks.filter(task => task.id !== deletedTaskId));
            break;
          default:
            break;
        }
      }
    }
  });

  const toggleFullScreen = () => {
    setFullScreen(prev => !prev);
  };

  /**
   * Function to handle opening/closing modals
   * @param {string} modalName - The name of the modal to toggle
   * @param {boolean} value - The state value to set for the modal (true or false)
   */
  const toggleModal = (modalName, value) => {
    setModals((prevState) => ({
      ...prevState,
      [modalName]: value,
    }));
  };



  //FullScreen if initialFile is set
  useEffect(() => {
    if (initialFile) {
      setFullScreen(true);
    }
  }, [initialFile]);

  useEffect(() => {
    if (projectId) {
      getProjectAndTasks({ variables: { projectId } });
      handleHideTaskDetails();
      resetProjectViews();
    }
  }, [projectId]);

  useEffect(() => {
    if (projectLoading) {
      clearProjectAndTasks();
    }
    else if (projectError) {
      console.error("** [ProjectContent] Error while fetching project:", projectError);
      clearProjectAndTasks();
    }
    else {
      const projectResult = projectData?.projects?.getProject;
      const tasksResult = projectData?.projects?.getTasks?.tasks || [];
      const userLookupResult = projectData?.projects?.listProjectUserCandidates || [];

      setUserLookup(userLookupResult);

      if (projectResult && tasksResult) {
        setProject(projectResult);
        setTasks(tasksResult);
      }
      else {
        clearProjectAndTasks();
      }
    }
  }, [projectData, projectError, projectLoading]);

  useEffect(() => {
    if (!deleteTask.status.loading) {
      toggleModal("confirmDeleteTaskModal", false);
      toggleModal("taskDetailsModal", false);
    }
  }, [deleteTask.status.loading]);



  const clearProjectAndTasks = () => {
    setProject();
    setTasks([]);
  }

  const cloneTask = (item) => {
    const task = cloneDeep(item);

    delete task.createdBy;
    delete task.id;
    delete task.nodes;
    delete task.parentTaskId;
    delete task.projectId;
    delete task.timestamp;
    delete task.reportedTime;
    delete task.reportedTimeLog;
    delete task.comments;

    if (task.assignments) {
      task.assignments = task.assignments.map(n => ({ userId: n.userId }));
    }

    return task;
  };

  const handleConfirmDeleteTask = () => {
    if (!!taskActionData) {
      const { action } = taskActionData;
      if (action === TaskAction.DELETE) {
        const { taskId } = taskActionData;
        handleDeleteTask(taskId);
      }
      else {
        console.warn(`** Expected action "${TaskAction.DELETE}", but got "${action}".`);
      }
    }
  };

  const handleDeleteTask = async (taskId) => {
    const handleDeleteError = (err = "") => {
      console.error("** error >", err);
      snackbar.error(<FormattedMessage id="common.genericErrorMessage" />);
    };

    const findTask = id => tasks.find(n => n.id === id);
    const findChildTasksIds = id => tasks.filter(n => n.parentTaskId === id).map(n => n.id);

    const getDescendantTaskIds = id => {
      let taskIds = findChildTasksIds(id);
      let i = 0;
      let taskId;

      while ((taskId = taskIds[i++])) {
        taskIds.push(...findChildTasksIds(taskId));
      }

      return taskIds;
    };

    const removeTask = id => {
      if (!findTask(id)) {
        console.warn("** Task not found!", { id });
        return;
      }

      var taskIds = [id, ...getDescendantTaskIds(id)];

      setTasks((prev) => prev.filter(task => !taskIds.includes(task.id)));
    };

    const index = tasks.findIndex(n => n.id === taskId);

    if (index < 0) {
      handleDeleteError("task not deleted (not found)");
      return;
    }

    await deleteTask.mutate({ projectId, taskId })
      .then(result => {
        if (result.success) {
          removeTask(taskId);
          handleHideTaskDetails();
        }
        else {
          handleDeleteError("task not deleted");
        }
      })
      .catch(err => {
        handleDeleteError(err);
      });
  };

  const handleHideTaskDetails = () => {
    handleShowTaskDetails(null, null);
  };

  const handleMoveTask = async (taskActionData) => {
    const handleMoveError = (err = "", ...rest) => {
      console.error("** error >", err, ...rest);
      snackbar.error(<FormattedMessage id="common.genericErrorMessage" />);
    };

    const moveLeft = (s, t, parentTaskId) => {
      var first = tasks.slice(0, t);
      var task = tasks[s];
      task.parentTaskId = parentTaskId;
      var middle = tasks.slice(t, s);
      var last = tasks.slice(s + 1);
      setTasks([...first, task, ...middle, ...last]);
    };

    const moveRight = (s, t, parentTaskId) => {
      var first = tasks.slice(0, s);
      var task = tasks[s];
      task.parentTaskId = parentTaskId;
      var middle = tasks.slice(s + 1, t + 1);
      var last = tasks.slice(t + 1);
      setTasks([...first, ...middle, task, ...last]);
    };

    const moveTaskInTaskList = (sourceIndex, targetIndex, parentTaskId) => {
      return (sourceIndex < targetIndex)
        ? moveRight(sourceIndex, targetIndex, parentTaskId)
        : moveLeft(sourceIndex, targetIndex + 1, parentTaskId);
    };

    const { action } = taskActionData;

    if (action === TaskAction.MOVE) {

      const { parentTaskId, insertAfterTaskId, taskId } = taskActionData;

      if (insertAfterTaskId === taskId) {
        //console.info("** did not move (same source and target)");
        return;
      }

      const sourceIndex = tasks.findIndex(n => n.id === taskId);
      if (sourceIndex < 0) {
        handleMoveError("task not moved (not found)", { sourceIndex });
        return;
      }

      const targetTaskId = insertAfterTaskId ?? parentTaskId;

      const targetIndex = targetTaskId
        ? tasks.findIndex(n => n.id === targetTaskId)
        : -1;

      if (targetTaskId && targetIndex < 0) {
        handleMoveError("task not moved (invalid target)", { targetIndex });
        return;
      }

      if (targetIndex === sourceIndex) {
        //console.info("** did not move (same source and target)");
        return;
      }

      await moveTask.mutate({ projectId, parentTaskId, insertAfterTaskId, taskId })
        .then(result => {
          if (result.success) {
            moveTaskInTaskList(sourceIndex, targetIndex, parentTaskId);
            handleHideTaskDetails();
          }
          else {
            handleMoveError("task not moved");
          }
        })
        .catch(err => {
          handleMoveError(err);
        });

    } else {
      console.warn("** unhandled action", { action });
    }
  };

  const handleSaveDetails = async ({ item, immediateTaskActionData }) => {
    const handleSaveError = (err = "") => {
      console.error("** error >", err);
      snackbar.error(<FormattedMessage id="common.errorOnSaving" />);
    };

    const insertTask = (index, task) => {
      var first = tasks.slice(0, index);
      var last = tasks.slice(index);
      setTasks([...first, task, ...last]);
    };

    const replaceTask = (index, task) => {
      var first = tasks.slice(0, index);
      var last = tasks.slice(index + 1);
      setTasks([...first, task, ...last]);
    };

    const { action } = (immediateTaskActionData || taskActionData);

    if (action === TaskAction.ADD || action === TaskAction.INSERT_NEW) {

      const { parentTaskId, insertAfterTaskId } = (immediateTaskActionData || taskActionData);

      const taskId = insertAfterTaskId ?? parentTaskId;

      let index = 0;

      if (action === TaskAction.INSERT_NEW) {
        index = tasks.length;
      }

      if (taskId) {
        index = tasks.findIndex(n => n.id === taskId) + 1;
        if (index < 1) {
          handleSaveError("task not created (invalid insertion point)");
          return;
        }
      }

      const task = cloneTask(item);

      await createTask.mutate({ projectId, parentTaskId, insertAfterTaskId, task })
        .then(result => {
          if (result.success) {
            insertTask(index, result.task);
            if (!modals.taskDetailsModal) {
              handleHideTaskDetails();
            }
            else {
              setTaskDetails(result.task);
              setTaskActionData({ action: TaskAction.EDIT, taskId: result.task.id });
            }
          }
          else {
            handleSaveError("task not created");
          }
        })
        .catch(err => {
          handleSaveError(err);
        });

    }
    else if (action === TaskAction.EDIT) {

      var index = tasks.findIndex(n => n.id === item.id);
      if (index < 0) {
        handleSaveError("task not found");
        return;
      }

      const { taskId } = (immediateTaskActionData || taskActionData);

      const task = cloneTask(item);

      await updateTask.mutate({ projectId, taskId, task })
        .then(result => {
          if (result.success) {
            replaceTask(index, result.task);
            if (!modals.taskDetailsModal) {
              handleHideTaskDetails();
            }
          }
          else {
            handleSaveError("task not updated");
          }
        })
        .catch(err => {
          handleSaveError(err);
        });

    }
    else {
      console.warn("** unhandled action", { action });
    }
  };

  const handleSaveError = (err = "") => {
    console.error("** error >", err);
    snackbar.error(<FormattedMessage id="common.errorOnSaving" />);
  };

  const handleShowTaskDetails = (item, columns) => {
    if (!item) {
      setShowTaskDetails(false);
      setTaskActionData();
    }
    else if (showTaskDetails) {
      if (taskDetails && taskDetails.id === item.id && item.id !== 0) {
        setShowTaskDetails(false);
        setTaskActionData();
      }
      else {
        setTaskDetails(item);
        setTaskDetailsColumns(columns);
      }
    }
    else {
      setShowTaskDetails(true);
      setTaskDetails(item);
      setTaskDetailsColumns(columns);
    }
  };

  const handleShowTaskDetailsModal = () => {
    toggleModal("taskDetailsModal", true);
    setShowTaskDetails(false);
  }

  const handleTaskAction = ({ action, columns, data }) => {
    switch (action) {
      case TaskAction.INSERT_NEW:
        handleSaveDetails({ item: data.task, immediateTaskActionData: { action, ...data } });
        break;
      case TaskAction.ADD:
      case TaskAction.EDIT:
        setTaskActionData({ action, ...data });
        handleShowTaskDetails(data.task, columns);
        break;
      case TaskAction.DELETE:
        setTaskActionData({ action, ...data });
        toggleModal("confirmDeleteTaskModal", true);
        break;
      case TaskAction.MOVE:
        setTaskActionData({ action, ...data });
        handleMoveTask({ action, ...data });
        break;
      case TaskAction.UPDATE:
        handleUpdateTask({ item: data.task });
        break;
      default:
        setTaskActionData();
        console.warn("** unhandled action", { action });
    }
  };

  const handleTimeLogAction = async (action, data) => {

    const replaceTask = (index, task) => {
      var first = tasks.slice(0, index);
      var last = tasks.slice(index + 1);
      setTasks([...first, task, ...last]);
    };

    if (action === TimeLogAction.ADD) {

      await reportTime({ id: 0, comment: "", projectId: data.task.projectId, taskId: data.task.id, reportedTime: data.minutes })
        .then(result => {

          if (result.success) {
            var index = tasks.findIndex(n => n.id === data.task.id);

            if (index < 0) {
              handleSaveError("task not found");
              return;
            }

            const task = cloneDeep(tasks[index]);

            task.reportedTimeLog.unshift(result.timeReport);

            task.reportedTime += result.timeReport.reportedMinutes;

            replaceTask(index, task);

            if (taskDetails && taskDetails.taskId === task.taskId) {

              var clonedDetails = cloneDeep(taskDetails);
              clonedDetails.reportedTimeLog = task.reportedTimeLog;
              clonedDetails.reportedTime = task.reportedTime;
              setTaskDetails(clonedDetails);
            }

          } else {
            handleSaveError("task not updated");
          }
        })
        .catch(err => {
          handleSaveError(err);
        });

      // ({ id, comment, projectId, taskId, reportedTime })
    }
    else if (action === TimeLogAction.UPDATE) {

      //onTimeLogAction(TimeLogAction.UPDATE, { task, reportItem, minutes, date });

      await reportTime({
        id: data.reportItem.id, comment: "",
        projectId: data.task.projectId, taskId: data.task.id,
        reportedTime: data.minutes,
        reportedAt: data.date
      })
        .then(result => {

          var index = tasks.findIndex(n => n.id === data.task.id);

          if (index < 0) {
            handleSaveError("task not found");
            return;
          }

          const task = cloneDeep(tasks[index]);

          const logIndex = task.reportedTimeLog.findIndex(log => log.id === result.timeReport.id);

          if (logIndex >= 0) {
            // Update the existing time log entry
            task.reportedTimeLog[logIndex].reportedMinutes = result.timeReport.reportedMinutes;
            task.reportedTimeLog[logIndex].reportedAt = result.timeReport.reportedAt;

            // Update the total reported time
            // Adjust the total reported time by adding the difference between the new and old reported times            

            const oldReportedTime = task.reportedTimeLog[logIndex].reportedMinutes;
            task.reportedTime += (result.timeReport.reportedMinutes - oldReportedTime);

            replaceTask(index, task);

            if (taskDetails && taskDetails.taskId === task.taskId) {

              var clonedDetails = cloneDeep(taskDetails);
              clonedDetails.reportedTimeLog = task.reportedTimeLog;
              clonedDetails.reportedTime = task.reportedTime;
              setTaskDetails(clonedDetails);
            }

          } else {
            handleSaveError("time log not found");
          }
        })
        .catch(err => {
          handleSaveError(err);
        });
    }
    else if (action === TimeLogAction.DELETE) {
      // handle delete action

      await deleteTime({ taskId: data.task.id, reportItemId: data.timeLogId })
        .then(result => {
          if (result) {

            var index = tasks.findIndex(n => n.id === data.task.id);

            if (index < 0) {
              handleSaveError("task not found");
              return;
            }

            const task = cloneDeep(tasks[index]);
            const logIndex = task.reportedTimeLog.findIndex(log => log.id === data.timeLogId);


            if (logIndex >= 0) {
              // Remove the specific time log entry
              const logToBeDeleted = task.reportedTimeLog[logIndex];
              task.reportedTime -= logToBeDeleted.reportedMinutes;
              task.reportedTimeLog.splice(logIndex, 1);
            } else {
              handleSaveError("time log not found");
              return;
            }

            replaceTask(index, task);


            if (taskDetails && taskDetails.taskId === task.taskId) {

              var clonedDetails = cloneDeep(taskDetails);
              clonedDetails.reportedTimeLog = task.reportedTimeLog;
              clonedDetails.reportedTime = task.reportedTime;
              setTaskDetails(clonedDetails);
            }

          }

        }).catch(err => {
          handleSaveError(err);
        });
    }
  };

  const handleUpdatedProject = (updatedProject) => {
    toggleModal("projectEditModal", false);
    setProject(updatedProject);

    // When a column is deleted, the corresponding field must be removed from all tasks.
    var updatedProjectFields = updatedProject.projectFields.map(n => n.fieldId);

    var updatedTasks = tasks.map(task => {
      var updatedTask = cloneDeep(task);
      updatedTask.values = updatedTask.values.filter(field => updatedProjectFields.includes(field.fieldId));
      return updatedTask;
    });

    setTasks(updatedTasks);

    onUpdated?.(updatedProject);
  }

  const handleUpdateTask = async ({ item }) => {

    const replaceTask = (index, task) => {
      var first = tasks.slice(0, index);
      var last = tasks.slice(index + 1);
      setTasks([...first, task, ...last]);
    };

    var index = tasks.findIndex(n => n.id === item.id);
    if (index < 0) {
      handleSaveError("task not found");
      return;
    }

    const task = cloneTask(item);
    const taskId = item.id;

    await updateTask.mutate({ projectId, taskId, task })
      .then(result => {
        if (result.success) {
          replaceTask(index, result.task);
        }
        else {
          handleSaveError("task not updated");
        }
      })
      .catch(err => {
        handleSaveError(err);
      });
  };



  /**
   * @component ProjectContentModals
   * @description Renders the modals based on the current modal states.
   * @returns {JSX.Element} The rendered modal components.
   */
  const ProjectContentModals = () => (
    <>
      {modals.confirmDeleteTaskModal && (
        <ConfirmDeleteModal
          loading={deleteTask.status.loading}
          onConfirmCloseModal={handleConfirmDeleteTask}
          open={modals.confirmDeleteTaskModal}
          setOpen={open => toggleModal("confirmDeleteTaskModal", open)}
        />
      )}

      {modals.projectEditModal && (
        <ProjectModal
          onClose={_ => toggleModal("projectEditModal", false)}
          onSaved={handleUpdatedProject}
          open={modals.projectEditModal}
          project={project}
        />
      )}

      {modals.taskDetailsModal && (
        <TaskDetailsModal
          columns={taskDetailsColumns}
          currentProject={project}
          disabled={taskLoading}
          enableEdit={enableEdit}
          onClose={() => {
            toggleModal("taskDetailsModal", false);
            handleShowTaskDetails();
          }}
          onDelete={(props) => deleteTaskAction(props.taskId)}
          onSave={handleSaveDetails}
          onTimeLogAction={handleTimeLogAction}
          open={modals.taskDetailsModal}
          task={taskDetails}
          userLookup={userLookup}
        />
      )}
    </>
  );

  if (projectError || projectLoading || !project) {
    return (
      <Box color="text.secondary" component="h1" m="auto">
        {projectLoading && (
          <CircularProgress color="inherit" size={60} sx={{ mr: 1 }} />
        )}
        {((projectError || (!projectLoading && (projectData?.projects?.getProject?.projectId !== projectId))) && (
          <FormattedMessage id="projectSelector.noProject" />
        ))}
      </Box>
    );
  };

  return (
    <Box
      component="div"
      sx={rootStyles}
    >
      <ProjectContentHead
        ref={projectContentHeadRef}
        onEditClick={_ => toggleModal("projectEditModal", true)}
        project={project}
      />

      <ProjectViewSelector
        ref={projectViewSelectorRef}
        onSelect={onSelectView}
        selectedIndex={activeTabIndex}
        slotProps={{
          root: { position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 2, mb: 1, pb: 1 },
        }}
        tabsData={projectTabs}
      />

      <ProjectViewPanel {...createPanelProps(ProjectViewType.Dashboard)}>
        {isActivePanel(ProjectViewType.Dashboard) &&
          <WorkspaceDashboard key={activeTabIndex} siteId={siteId} projectId={projectId} route={location.pathname} />
        }
      </ProjectViewPanel>

      <ProjectViewPanel {...createPanelProps(ProjectViewType.Tasks)}>
        {isActivePanel(ProjectViewType.Tasks) &&
          <>
            <TableView
              disabled={taskLoading}
              enableEdit={enableEdit}
              key={activeTabIndex}
              onProjectUpdate={handleUpdatedProject}
              onTaskAction={handleTaskAction}
              onTimeLogAction={handleTimeLogAction}
              project={project}
              tasks={tasks}
              userLookup={userLookup}
              viewColumnIds={viewSettings.viewColumnIds}
            />
            <TaskDetailsDrawer
              key={activeTabIndex + 100}
              onClose={handleHideTaskDetails}
              onShowDetailsModal={handleShowTaskDetailsModal}
              show={showTaskDetails}
            >
              {showTaskDetails &&
                <TaskDetailsBase
                  columns={taskDetailsColumns}
                  currentProject={project}
                  disabled={taskLoading}
                  enableEdit={enableEdit}
                  item={taskDetails}
                  onDelete={(props) => deleteTaskAction(props.taskId)}
                  onSave={handleSaveDetails}
                  onTimeLogAction={handleTimeLogAction}
                  renderComponent={TaskDetailsColumn}
                  userLookup={userLookup}
                />
              }
            </TaskDetailsDrawer>
          </>
        }
      </ProjectViewPanel>

      <ProjectViewPanel {...createPanelProps(ProjectViewType.Files)}>
        {isActivePanel(ProjectViewType.Files) &&
          <ProjectFileView key={activeTabIndex} rootFolderId={project.linkedFolderId} />
        }
      </ProjectViewPanel>

      <ProjectViewPanel {...createPanelProps(ProjectViewType.Reports)}>
        {isActivePanel(ProjectViewType.Reports) &&
          <Box>
            <ReportsPage
              fullScreen={fullScreen}
              parentHeight={reportsPageParentHeight}
              initialFile={initialFile}
              key={activeTabIndex}
              toggleFullScreen={toggleFullScreen}
              projectName={project}
            />
          </Box>
        }
      </ProjectViewPanel>

      <ProjectViewPanel {...createPanelProps(ProjectViewType.Kanban)}>
        {isActivePanel(ProjectViewType.Kanban) &&
          <>
            <Kanban
              enableEdit={enableEdit}
              key={activeTabIndex}
              onTaskAction={handleTaskAction}
              project={project}
              selectedFieldId={viewSettings.kanbanFieldId}
              tasks={tasks}
              userLookup={userLookup}
            //viewColumnIds={viewSettings.viewColumnIds}
            />
            <TaskDetailsDrawer
              key={activeTabIndex + 100}
              onClose={handleHideTaskDetails}
              onShowDetailsModal={handleShowTaskDetailsModal}
              show={showTaskDetails}
            >
              {showTaskDetails &&
                <TaskDetailsBase
                  columns={taskDetailsColumns}
                  currentProject={project}
                  disabled={taskLoading}
                  enableEdit={enableEdit}
                  item={taskDetails}
                  onDelete={(props) => deleteTaskAction(props.taskId)}
                  onSave={handleSaveDetails}
                  onTimeLogAction={handleTimeLogAction}
                  renderComponent={TaskDetailsColumn}
                  userLookup={userLookup}
                />
              }
            </TaskDetailsDrawer>
          </>
        }
      </ProjectViewPanel>

      <ProjectContentModals />

    </Box>
  );
};

ProjectContent.displayName = "ProjectContent";

export default ProjectContent;
